import React, {
    useRef, useState, useEffect, useLayoutEffect,
} from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { GrDrag } from 'react-icons/gr';

const DraggableElement = ({
    children,
    index,
    swapElements,
    className,
    onClick,
    draggableType,
    element,
    dragHandle,
    ...props
}) => {
    const ref = useRef(null);
    const [wrapperElement, setWrapperElement] = useState(element || 'div');
    const [dragHandleElement, setDragHandleElement] = useState(dragHandle || 'div');
    const [isMounted, setIsMounted] = useState(false);

    const [{ handlerId }, drop] = useDrop({
        accept: draggableType,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === undefined || hoverIndex === undefined) {
                return;
            }
            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }
            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Only perform the move when the mouse has crossed half of the items height
            if (dragHandleElement === null) {
                const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
                const hoverClientX = clientOffset.x - hoverBoundingRect.left;
                // Dragging sideways
                if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
                    return;
                }
                if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
                    return;
                }
            } else {
                // Get vertical middle
                const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
                // Get pixels to the top
                const hoverClientY = clientOffset.y - hoverBoundingRect.top;
                // Dragging downwards
                if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                    return;
                }
                // Dragging upwards
                if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                    return;
                }
            }
            // Time to actually perform the action
            swapElements(dragIndex, hoverIndex);
            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });
    const [{ isDragging }, drag, preview] = useDrag({
        type: draggableType,
        item: {
            type: draggableType,
            index,
        },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    });
    drag(drop(ref));

    useLayoutEffect(() => {
        if (!ref.current) {
            return;
        }
        const parentElement = ref.current.parentElement.parentElement;
        if (parentElement.tagName === 'TBODY') {
            setWrapperElement('tr');
            setDragHandleElement('td');
        }
        if (parentElement.classList.contains('nav-tabs')) {
            setDragHandleElement(null);
        }
    }, []);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const WrapperElement = wrapperElement;
    const DragHandleElement = dragHandleElement;

    return (
        <WrapperElement
            {...props}
            ref={preview}
            data-handler-id={handlerId}
            data-type={draggableType}
            key={index}
            onClick={onClick}
            className={`${className || ''} ${dragHandleElement === 'div' ? 'd-flex flex-row' : ''} ${!isMounted ? 'd-none' : ''}`}
        >
            {
                dragHandleElement === null ? (
                    <div
                        ref={ref}
                        className="h-100"
                    >
                        {children}
                    </div>
                ) : (
                    <>
                        <DragHandleElement
                            width="10"
                            style={{
                                cursor: 'move',
                            }}
                            ref={ref}
                        >
                            <GrDrag />
                        </DragHandleElement>
                        {children}
                    </>
                )
            }
        </WrapperElement>
    );
};

export default DraggableElement;
